import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAdminPanelStore } from "features/AdminPanel/store/useAdminPanelStore";
import { PAYMENT_OPTION_KEYS } from "features/Account/modules/PaymentOptions/constants/PaymentOptions.constants";
import { ADMIN_PANEL_SECTION_CONFIG_FE_STATUS } from "features/AdminPanel/constants/AdminPanel.constants";
import {
  UPDATE_ADMIN_PANEL_SETTINGS,
  UPDATE_ADMIN_PANEL_SETTINGS_LISITNG_LEVEL,
} from "features/AdminPanel/constants/AdminPanel.api";
import { useCustomMutation } from "data/modules/api/hooks/useCustomMutation";
import { useInternationalPaymentStore } from "../stores/useInternationalPaymentStore";
import {
  getInitialFormState,
  getInitialMetaState,
  validateFields,
  isFormValid,
} from "./useInternationalPaymentForm";
import {
  INTERNATIONAL_PAYMENT_FIELDS_CONFIG,
  INTERNATIONAL_PAYMENT_TEXTS,
} from "../../modules/InternationalPayment/InternationalPayment.constants";
import { getSectionStates } from "features/AdminPanel/modules/utils/AdminPanel.utils";

export const useEnableInternationalPaymentSwitch = () => {
  const INTERNATIONAL_PAYMENT_KEY = PAYMENT_OPTION_KEYS.INTERNATIONAL_PAYMENT;

  const params = useParams();
  const listingUuid = params?.listing_uuid ?? null;

  const sectionConfig = useAdminPanelStore((state) => state.sectionConfig);
  const setSectionConfig = useAdminPanelStore(
    (state) => state.setSectionConfig
  );
  const { status } = sectionConfig[INTERNATIONAL_PAYMENT_KEY] || {};
  const { disableSwitch } = getSectionStates({ status });

  const { isEnabled, addressData, initFromConfig, setListingUuid } =
    useInternationalPaymentStore();

  useEffect(() => {
    const configValue = sectionConfig[INTERNATIONAL_PAYMENT_KEY]?.value || {};
    initFromConfig(configValue, listingUuid);
    setListingUuid(listingUuid);
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  const [formValues, setFormValues] = useState(getInitialFormState);
  const [formMeta, setFormMeta] = useState(getInitialMetaState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const url = listingUuid
    ? UPDATE_ADMIN_PANEL_SETTINGS_LISITNG_LEVEL
    : UPDATE_ADMIN_PANEL_SETTINGS;

  const { mutateAsync } = useCustomMutation({ url });

  const setStatus = (feStatus, extraValue = {}) => {
    setSectionConfig({
      [INTERNATIONAL_PAYMENT_KEY]: {
        ...sectionConfig[INTERNATIONAL_PAYMENT_KEY],
        ...extraValue,
        FE_METADATA: { UPDATION_STATUS: { STATUS: feStatus } },
      },
    });
  };

  const buildPayload = (isEnable, address = {}) => ({
    feature_key: INTERNATIONAL_PAYMENT_KEY,
    feature_value: { is_enabled: isEnable, ...address },
    ...(listingUuid && { listing_uuid: listingUuid }),
  });

  const handleToggle = async (newValue) => {
    if (newValue) {
      if (!addressData) {
        setFormValues(getInitialFormState());
        setFormMeta(getInitialMetaState());
        setModalOpen(true);
        return;
      }

      setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.SAVING);
      try {
        await mutateAsync(buildPayload(true, addressData));
        setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.SAVED, {
          value: { is_enabled: true, ...addressData },
        });
        useInternationalPaymentStore.getState().setIsEnabled(true);
      } catch {
        setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.ERROR);
      }
      return;
    }

    setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.SAVING);
    try {
      await mutateAsync(buildPayload(false, addressData ?? {}));
      setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.SAVED, {
        value: {
          ...sectionConfig[INTERNATIONAL_PAYMENT_KEY]?.value,
          is_enabled: false,
        },
      });
      useInternationalPaymentStore.getState().setIsEnabled(false);
    } catch {
      setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.ERROR);
    }
  };

  const handleChange = (val, name) => {
    setFormValues((prev) => ({ ...prev, [name]: val }));
    setFormMeta((prev) => ({
      ...prev,
      [name]: { touched: true, error: val?.trim() ? "" : prev[name]?.error },
    }));
  };

  const handleBlur = (name) => {
    setFormMeta((prev) => ({
      ...prev,
      [name]: {
        touched: true,
        error:
          INTERNATIONAL_PAYMENT_FIELDS_CONFIG[name]?.required &&
          !formValues[name]?.trim()
            ? INTERNATIONAL_PAYMENT_TEXTS.REQUIRED_ERROR
            : "",
      },
    }));
  };

  const handleSubmit = async () => {
    const meta = validateFields(formValues);
    setFormMeta(meta);
    if (!isFormValid(formValues)) return;

    setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.SAVING);
    setIsSubmitting(true);
    try {
      await mutateAsync(buildPayload(true, formValues));
      setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.SAVED, {
        value: { is_enabled: true, ...formValues },
      });
      useInternationalPaymentStore.getState().setIsEnabled(true);
      useInternationalPaymentStore.getState().setAddressData(formValues);
      setModalOpen(false);
    } catch {
      setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isEnabled,
    disableSwitch,
    modalOpen,
    setModalOpen,
    formValues,
    formMeta,
    isSubmitting,
    isSubmitDisabled: !isFormValid(formValues) || isSubmitting,
    handleToggle,
    handleChange,
    handleBlur,
    handleSubmit,
  };
};
